<script lang="ts">
  // show list of private users
  // use modals for new, edit and delete
  // new page for results

  import { db, auth } from "../firebase";
  import { loggedIn, pwaNameForResults } from "../stores/user-store.svelte";

  import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
  } from "@firebase/firestore";

  import type { Unsubscribe } from "@firebase/firestore";

  import { onMount, onDestroy } from "svelte";

  import { DocType, Role, Pages } from "../enums";
  import type { StudentUserData, EditUserEmail, User } from "../types";

  import { Icons } from "../utilities/icons";

  import { route } from "../stores/route.svelte";

  import {
    user,
    center,
    pwaEmailForResults,
  } from "../stores/user-store.svelte";
  import { editPWASettings } from "../stores/edit-pwa-settings.svelte";

  import IconButton from "../components/IconButton.svelte";
  import ListBox from "../components/ListBox.svelte";

  import NewUserModal from "../modals/NewUserModal.svelte";
  import DeleteUserModal from "../modals/DeleteUserModal.svelte";
  import WaitModal from "../modals/WaitModal.svelte";
  import YesNoModal from "../modals/YesNoModal.svelte";

  // STATE -----------------------------------------------------------

  let unsubscribe: Unsubscribe;
  let privates: Array<User & StudentUserData & EditUserEmail> = [];

  let showNewUserModal = $state(false);
  let showDeleteUserModal = $state(false);
  let showWaitModal = $state(false);
  let showYesNoModal = $state(false);

  // list of users to display, construct from firestore database
  let listItems: Array<string> = $state([]);
  // selected in list
  let selectedIndex: number | null = $state(null);

  let numberOfActiveUsers = $state(0);
  let hasFreeLicenses = $state(false);

  // email and name for delete to avoid being undefined after delete
  let emailForDelete = $state("");
  let nameForDelete = $state("");

  // FUNCTIONS -------------------------------------------------------

  const updateNumberOfActiveUsers = (): number => {
    let tempNumberOfActiveUsers = 0;
    privates.forEach((element) => {
      if (element.active) {
        tempNumberOfActiveUsers++;
      }
    });
    hasFreeLicenses = tempNumberOfActiveUsers < center.value.maxActiveUsers;
    editPWASettings.hasFreeLicenses = hasFreeLicenses;
    return tempNumberOfActiveUsers;
  };

  // database operations ---

  const getRealtimeUpdates = () => {
    const collectionRef = collection(db, DocType.Users);
    const collectionQuery = query(
      collectionRef,
      where("centerUid", "==", user.value.centerUid),
      where("role", "==", Role.Private),
      orderBy("name"),
    );
    unsubscribe = onSnapshot(collectionQuery, (querySnapshot) => {
      privates = [];
      listItems = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        privates.push({
          email: doc.id,
          name: data.name,
          uid: data.uid,
          centerUid: data.centerUid,
          role: data.role,
          active: data.active,
          sessionsCompleted: data.sessionsCompleted,
          currentLevel: data.currentLevel,
          completedOnCurrentLevel: data.completedOnCurrentLevel,
          settings: data.settings,
        });
        const active = data.active ? "aktiv" : "deaktiveret";
        listItems.push(data.name + " - " + doc.id + " - " + active);
      });
      showWaitModal = false;
      numberOfActiveUsers = updateNumberOfActiveUsers();
    });
  };

  // button clicks ---

  const backButtonClicked = () => {
    route.current = Pages.AdminStart;
  };

  const newPWAButtonClicked = () => {
    showNewUserModal = true;
  };

  const editPWAButtonClicked = () => {
    if (selectedIndex === null) {
      console.error("Fejl i selectedIndex for editPWAButtonClicked");
      return;
    }
    editPWASettings.email = privates[selectedIndex!].email;
    editPWASettings.name = privates[selectedIndex!].name;
    editPWASettings.active = privates[selectedIndex!].active;
    editPWASettings.settings = privates[selectedIndex!].settings;
    editPWASettings.level = privates[selectedIndex!].currentLevel;
    editPWASettings.completedOnCurrentLevel =
      privates[selectedIndex].completedOnCurrentLevel;
    route.return = Pages.PrivateEdit;
    route.current = Pages.Settings;
  };

  const deletePWAButtonClicked = () => {
    if (selectedIndex === null) {
      console.error("Fejl i selectedIndex for deletePWAButtonClicked");
      return;
    }
    emailForDelete =
      privates.length === 0 || selectedIndex === null
        ? ""
        : privates[selectedIndex].email;
    nameForDelete =
      privates.length === 0 || selectedIndex === null
        ? ""
        : privates[selectedIndex].name;
    showDeleteUserModal = true;
  };

  const resultsButtonClicked = () => {
    pwaEmailForResults.value = privates[selectedIndex!].email;
    pwaNameForResults.value = privates[selectedIndex!].name;
    route.return = Pages.PrivateEdit;
    route.current = Pages.ResultList;
  };

  // event functions ---

  const newUserWasCreated = () => {
    selectedIndex = null;
    showNewUserModal = false;
  };

  const userWasDeleted = () => {
    showDeleteUserModal = false;
    selectedIndex = null;
  };

  // log-out ------------------

  const doLogOut = () => {
    auth.signOut().then(function () {
      localStorage.setItem("rememberMeOnThisDevice", "no");
      loggedIn.value = false;
      route.current = Pages.Login;
    });
  };

  const logOutClicked = () => {
    showYesNoModal = true;
  };

  // LIFE CYCLE --------------------------------------------------

  onMount(() => {
    showWaitModal = true;
    getRealtimeUpdates();
  });

  onDestroy(() => {
    unsubscribe();
  });
</script>

<!-- HTML ===================================================== -->

<main data-page="private-edit">
  <div class="heading">
    <h1>Private brugere - indstillinger og resultater</h1>
  </div>

  <ListBox items={listItems} bind:selectedIndex />

  <div class="bottom-area">
    {#if user.value.role === Role.Admin || user.value.role === Role.Super}
      <span class="button-spacing">
        <IconButton
          onclick={backButtonClicked}
          textMediumScaled={true}
          icon={Icons.ArrowLeft}
        >
          {#snippet message()}Tilbage{/snippet}
        </IconButton>
      </span>
    {/if}
    <span class="button-spacing">
      <IconButton
        onclick={newPWAButtonClicked}
        textMediumScaled={true}
        icon={Icons.Plus}
      >
        {#snippet message()}Ny{/snippet}
      </IconButton>
    </span>
    <span class="button-spacing">
      <IconButton
        disabled={selectedIndex === null}
        onclick={editPWAButtonClicked}
        textMediumScaled={true}
        icon={Icons.CogLight}
      >
        {#snippet message()}
          Indstillinger{/snippet}
      </IconButton>
    </span>
    <span class="button-spacing">
      <IconButton
        disabled={selectedIndex === null}
        onclick={deletePWAButtonClicked}
        textMediumScaled={true}
        icon={Icons.TrashAltRegular}
      >
        {#snippet message()}Slet{/snippet}
      </IconButton>
    </span>
    <span class="button-spacing">
      <IconButton
        disabled={selectedIndex === null}
        onclick={resultsButtonClicked}
        textMediumScaled={true}
        icon={Icons.FileAltRegular}
      >
        {#snippet message()}Resultater{/snippet}
      </IconButton>
    </span>
    {#if user.value.role === Role.SLT}
      <span class="button-spacing log-out-button">
        <IconButton
          textMediumScaled={true}
          icon={Icons.SignOutAlt}
          onclick={logOutClicked}
        >
          {#snippet message()}Log ud{/snippet}
        </IconButton>
      </span>
    {/if}
  </div>
</main>

{#if showNewUserModal}
  <NewUserModal
    userType={Role.Private}
    newUserDialogCanceled={() => (showNewUserModal = false)}
    newUserCreated={newUserWasCreated}
  />
{/if}

{#if showDeleteUserModal}
  <DeleteUserModal
    userType={Role.Private}
    email={emailForDelete}
    name={nameForDelete}
    deleteUserDialogCanceled={() => (showDeleteUserModal = false)}
    userDeleted={userWasDeleted}
  />
{/if}

{#if showWaitModal}
  <WaitModal />
{/if}

{#if showYesNoModal}
  <YesNoModal no={() => (showYesNoModal = false)} yes={doLogOut}
    >{#snippet message()}Vil du logge ud?{/snippet}</YesNoModal
  >
{/if}

<!-- CSS ===================================================== -->
<style>
  main {
    height: calc(var(--vh, 1vh) * 100);
    display: flex;
    flex-direction: column;
    padding: 1vmin;
  }
  h1 {
    font-size: 3vmin;
  }
  .heading {
    margin-bottom: 1vmin;
  }
  .bottom-area {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    border-top: 2px solid var(--key-hover-border-color);
    margin-top: 1vmin;
  }
  .button-spacing {
    padding-top: 1vmin;
    padding-right: 1vmin;
  }
  .log-out-button {
    margin-left: auto;
  }
</style>
